<template>
  <div v-if="isOpen" class="fixed inset-0 z-[200] flex items-center justify-center px-4 overflow-y-auto bg-black/50 backdrop-blur-sm" @click.self="closeModal">
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-3xl my-8 transform transition-all animate-in fade-in zoom-in duration-300 text-left relative overflow-visible max-h-[80vh] flex flex-col">
      <!-- Modal Header -->
      <div class="px-6 py-4 bg-[#0d4169] text-white flex items-center justify-between rounded-t-xl">
        <h2 class="text-lg font-bold m-0">Select Items for {{ ingredient.stockCategory }}</h2>
        <button @click="closeModal" class="p-1 hover:bg-white/10 rounded-full transition-colors text-white">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>

      <!-- Modal Body -->
      <div class="p-6 flex-1 overflow-y-auto">
        <!-- Search Bar -->
        <div class="flex justify-end items-center mb-4">
          <input
            v-model="searchQuery"
            type="text"
            class="w-[300px] px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white"
            placeholder="Search by item code or name..."
          >
        </div>

        <!-- Table -->
        <table class="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-sm">
          <thead>
            <tr>
              <th>No.</th>
              <th>Item Code</th>
              <th>Item Name</th>
              <th>Category</th>
              <th>Unit</th>
              <th>Unit Of Measurement</th>
              <th>Glass Amount</th>
              <th>Ingredient</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in filteredItems" :key="item.id" class="hover:bg-slate-100/50 transition-colors border-b">
              <td>{{ index + 1 }}</td>
              <td>{{ item.itemCode }}</td>
              <td>{{ item.itemName }}</td>
              <td>{{ item.category?.name }}</td>
              <td>{{ item.unit }}</td>
              <td>
                <select
                  v-model="item.unitProduction"
                  @change="updateItemField(item, 'unitProduction', item.unitProduction)"
                  class="w-[100px] px-2 py-1 text-xs border border-[#cbd5e1] rounded outline-none focus:border-[#0d4169] bg-white cursor-pointer"
                >
                  <option value="" disabled>Select Unit</option>
                  <option v-for="unit in unitsProduction" :key="unit" :value="unit">
                    {{ unit }}
                  </option>
                </select>
              </td>
              <td>
                <input
                  v-if="item.unitProduction === 'Glass'"
                  type="number"
                  min="0"
                  step="0.01"
                  v-model.number="item.glassAmount"
                  @change="handleGlassAmountChange(item)"
                  class="w-[80px] px-2 py-1 text-xs border border-[#cbd5e1] rounded outline-none focus:border-[#0d4169] bg-white text-right"
                  placeholder="0"
                >
              </td>
              <td class="text-center">
                <input
                  type="checkbox"
                  :checked="selectedSet.has(item.id)"
                  @change="toggleItemSelection(item)"
                  class="w-4 h-4 rounded border-slate-300 text-[#0d4169] focus:ring-[#0d4169] cursor-pointer"
                >
              </td>
            </tr>
            <tr v-if="filteredItems.length === 0">
              <td colspan="7" style="text-align:center; color:#94a3b8; padding: 48px 15px; font-style: italic;">
                No items found.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Modal Footer -->
      <div class="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-between items-center">
        <div class="text-sm text-slate-600">
          {{ selectedItems.length }} item{{ selectedItems.length !== 1 ? 's' : '' }} selected
        </div>
        <div class="flex gap-3">
          <button @click="closeModal" class="px-6 py-2 bg-slate-500 text-white text-sm font-medium rounded-md hover:bg-slate-600 transition-all">
            Cancel
          </button>
          <button
            @click="confirmSelection"
            :disabled="isConfirming"
            class="min-w-[100px] px-6 py-2 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center gap-2"
          >
            <div
              v-if="isConfirming"
              class="h-5 w-5 border-2 border-white/30 border-t-transparent rounded-full animate-spin"
            ></div>
            <span v-if="!isConfirming">Confirm Selection</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, defineProps, defineEmits } from 'vue'
import { apiFetch } from '../../../utils/api'

const unitsProduction = [
  'M.L.',
  'Gram',
  'Pieces',
  'Bottle',
  'Glass',
  
]

// Props
const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  },
  ingredient: {
    type: Object,
    default: () => ({})
  },
  itemsList: {
    type: Array,
    default: () => []
  }
})

// Emits
const emit = defineEmits(['close', 'confirm'])

// Reactive data
const selectedItems = ref([])
const initialSelectedItems = ref(new Set())
const initialGlassAmounts = ref(new Map())
const searchQuery = ref('')
const isConfirming = ref(false)

const selectedSet = computed(() => new Set(selectedItems.value))

function itemIsIngredient(item) {
  return item?.isIngredient === 1 || item?.isIngredient === '1'
    || item?.is_ingredient === 1 || item?.is_ingredient === '1'
}

function syncInitialSelectionFromList() {
  initialSelectedItems.value = new Set(
    (props.itemsList || []).filter(itemIsIngredient).map((i) => i.id)
  )
  selectedItems.value = [...initialSelectedItems.value]
  initialGlassAmounts.value = new Map(
    (props.itemsList || []).map((it) => [it.id, Number(it.glassAmount) || 0])
  )
}

// Computed
const filteredItems = computed(() => {
  const source = Array.isArray(props.itemsList) ? [...props.itemsList] : []
  source.sort((a, b) => {
    const nameA = String(a?.itemName || '').toLowerCase()
    const nameB = String(b?.itemName || '').toLowerCase()
    return nameA.localeCompare(nameB)
  })

  const q = String(searchQuery.value || '').toLowerCase().trim()
  if (!q) return source

  return source.filter(item =>
    String(item?.itemCode || '').toLowerCase().includes(q) ||
    String(item?.itemName || '').toLowerCase().includes(q)
  )
})

// Methods
function closeModal() {
  emit('close')
}

function toggleItemSelection(item) {
  const index = selectedItems.value.indexOf(item.id)
  const isNowChecked = index === -1
  
  if (!isNowChecked) {
    selectedItems.value.splice(index, 1)
  } else {
    selectedItems.value.push(item.id)
  }

  // Sync locally and to database
  item.isIngredient = isNowChecked ? 1 : 0
  // keep backend field in sync for lists that use is_ingredient
  item.is_ingredient = item.isIngredient
}

async function updateItemField(item, field, value) {
  try {
    const payload = {
      [field]: value
    }
    const response = await apiFetch(`/items/${item.id}`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    })
    
    if (!response.ok) {
      console.error(`Failed to update ${field} for item ${item.id}`)
      // Fallback: Show toast error (if your project uses a global showToast)
      // showToast('Failed to update database', 'error')
    }
  } catch (error) {
    console.error('Error updating item on server:', error)
  }
}

function handleGlassAmountChange(item) {
  const val = Number(item.glassAmount)
  const safe = Number.isFinite(val) && val >= 0 ? val : 0
  item.glassAmount = safe
  updateItemField(item, 'glassAmount', safe)
}

async function confirmSelection() {
  if (isConfirming.value) return

  isConfirming.value = true
  try {
    const currentSet = new Set(selectedItems.value)
    const initialSet = initialSelectedItems.value
    const selectIds = selectedItems.value.filter((id) => !initialSet.has(id))
    const deselectIds = [...initialSet].filter((id) => !currentSet.has(id))
    const selectIdSet = new Set(selectIds)

    const selectedItemObjects = props.itemsList.filter((item) => currentSet.has(item.id))

    const glassAmountUpdates = selectedItemObjects
      .filter((it) => it.unitProduction === 'Glass')
      .map((it) => {
        const val = Number(it.glassAmount)
        const safe = Number.isFinite(val) && val >= 0 ? val : 0
        it.glassAmount = safe
        return { itemId: it.id, glassAmount: safe }
      })
      .filter((row) => {
        const initial = initialGlassAmounts.value.get(row.itemId)
        return selectIdSet.has(row.itemId) || initial === undefined || initial !== row.glassAmount
      })

    const hasChanges = selectIds.length > 0 || deselectIds.length > 0 || glassAmountUpdates.length > 0

    if (hasChanges) {
      const bulkRes = await apiFetch('/items/bulk-ingredient-selection', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ selectIds, deselectIds, glassAmountUpdates }),
      })
      if (!bulkRes.ok) {
        const err = await bulkRes.json().catch(() => ({}))
        throw new Error(err.message || 'Failed to save ingredient selection')
      }

      const changedIds = new Set([...selectIds, ...deselectIds])
      for (const item of props.itemsList) {
        if (!changedIds.has(item.id)) continue
        const isSelected = currentSet.has(item.id)
        const nextValue = isSelected ? 1 : 0
        item.isIngredient = nextValue
        item.is_ingredient = nextValue
      }
    }

    if (selectIds.length > 0) {
      const newSelectIds = selectIds.map((id) => Number(id)).filter((n) => Number.isFinite(n))
      try {
        await apiFetch('/material-selection/delete-by-items', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ itemIds: newSelectIds })
        })
      } catch (error) {
        console.error('Failed to delete material selections for ingredient items:', error)
      }
    }

    emit('confirm', selectedItemObjects)
    closeModal()
  } catch (error) {
    console.error('Error confirming ingredient selection:', error)
  } finally {
    isConfirming.value = false
  }
}

// Lifecycle
onMounted(() => {
  if (props.isOpen) {
    syncInitialSelectionFromList()
  }
})

// Watch for modal opening
watch(() => props.isOpen, (newVal) => {
  if (newVal) {
    // Ensure default glassAmount exists on items for binding
    (props.itemsList || []).forEach(it => {
      if (it.glassAmount === undefined || it.glassAmount === null || Number.isNaN(Number(it.glassAmount))) {
        it.glassAmount = 0
      }
    })

    syncInitialSelectionFromList()
  }
})
</script>

<style scoped>
table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.75rem;
  color: #1e293b;
}

th, td {
  padding: 8px 10px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
}

th {
  background: #f1f5f9;
  cursor: default;
  font-weight: 600;
}

tbody tr:hover {
  background: #f1f5f9;
}
</style>